/*
This software is Copyright ©️ 2020 The University of Southern California. All Rights Reserved. 
Permission to use, copy, modify, and distribute this software and its documentation for educational, research and non-profit purposes, without fee, and without a written agreement is hereby granted, provided that the above copyright notice and subject to the full license file found in the root of this software deliverable. Permission to make commercial use of this software may be obtained by contacting:  USC Stevens Center for Innovation University of Southern California 1150 S. Olive Street, Suite 2300, Los Angeles, CA 90115, USA Email: accounting@stevens.usc.edu

The full terms of this copyright and license should always be found in the root directory of this software deliverable as "license.txt" and if these terms are not found with this software, please contact the USC Stevens Center for the full license.
*/

import YAML from "yaml";
import * as actions from ".";
import { useAppDispatch, useAppSelector } from "../../hooks";
import { Edits, Goal, LoadStatus } from "../../../types";
import { readYamlFile } from "../../../utils";

export interface UseWithGoals {
  goals: Record<string, Goal>;
  goalEdits: Record<string, Edits<Goal>>;
  goalLoadStatus: LoadStatus;
  loadAllGoals: () => Promise<Goal[]>;
  loadGoalFromYaml: (data: File | string) => Promise<Goal>;
  createGoal: (newData: Partial<Goal>) => Promise<Goal>;
  deleteGoal: (goal: Goal) => Promise<void>;
  updateGoal: (newData: Partial<Goal>) => Promise<Goal>;
  revertGoal: (id: string) => Promise<Goal>;
}

export default function useWithGoals(): UseWithGoals {
  const { topics: allTopics } = useAppSelector((state) => state.topics);
  const { surveys: allSurveys } = useAppSelector((state) => state.surveys);
  const { goals, goalLoadStatus, goalEdits } = useAppSelector(
    (state) => state.goals
  );
  const dispatch = useAppDispatch();

  async function loadAllGoals(): Promise<Goal[]> {
    if (goalLoadStatus === LoadStatus.IN_PROGRESS) return [];
    return await dispatch(actions.loadGoals()).unwrap();
  }

  async function loadGoalFromYaml(file: File | string): Promise<Goal> {
    let data: Goal;
    if (typeof file === "string") data = YAML.parse(file);
    else data = await readYamlFile(file);
    const id = typeof data.id === "string" ? data.id : "";
    const name = typeof data.name === "string" ? data.name : "";
    const desc = typeof data.desc === "string" ? data.desc : "";
    const hidden = typeof data.hidden === "boolean" ? data.hidden : false;
    const deleted = typeof data.deleted === "boolean" ? data.deleted : false;
    const org = typeof data.org === "string" ? data.org : "";
    const topics = [];
    if (Array.isArray(data.topics)) {
      for (const l of data.topics) {
        if (typeof l === "string" && allTopics[l]) {
          topics.push(l);
        }
      }
    }
    const plans = [];
    if (Array.isArray(data.plans)) {
      for (const l of data.plans) {
        if (typeof l === "string") {
          plans.push(l);
        }
      }
    }
    const survey =
      typeof data.survey === "string" && allSurveys[data.survey]
        ? data.survey
        : undefined;
    return {
      id,
      name,
      desc,
      hidden,
      deleted,
      org,
      topics,
      plans,
      survey,
      apiOverrides: data.apiOverrides || [],
      learnerTypes: data.learnerTypes || [],
    };
  }

  async function createGoal(data: Partial<Goal>): Promise<Goal> {
    if (!data.name) throw new Error("name required");
    if (!data.desc) throw new Error("desc required");
    return await dispatch(
      actions.addOrUpdateGoal({
        id: data.id || "",
        name: data.name || "",
        desc: data.desc || "",
        hidden: Boolean(data.hidden),
        deleted: Boolean(data.deleted),
        topics: data.topics || [],
        plans: data.plans || [],
        survey: data.survey,
        org: data.org,
        apiOverrides: data.apiOverrides || [],
      })
    ).unwrap();
  }

  async function deleteGoal(goal: Goal): Promise<void> {
    await dispatch(actions.deleteGoal(goal.id));
  }

  async function updateGoal(data: Partial<Goal>): Promise<Goal> {
    return await dispatch(actions.addOrUpdateGoal(data)).unwrap();
  }

  async function revertGoal(id: string): Promise<Goal> {
    return await dispatch(actions.revertGoal(id)).unwrap();
  }

  return {
    goals,
    goalEdits,
    goalLoadStatus,
    loadAllGoals,
    loadGoalFromYaml,
    createGoal,
    deleteGoal,
    updateGoal,
    revertGoal,
  };
}
